import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Select,
  Text,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ClipboardTask20Regular } from '@fluentui/react-icons';
import ContentSkeleton from '../../ContentSkeleton/ContentSkeleton';
import TitledCard from '../../Layout/TitledCard';
import {
  actualFields,
  type ActualFacts,
  type ActualExpense,
  type ActualsResponse,
  type WorkActualsServices,
} from './types';

const useStyles = makeStyles({
  content: {
    display: 'grid',
    gap: tokens.spacingVerticalL,
    minWidth: 0,
    '& p': { margin: 0 },
  },
  status: {
    display: 'grid',
    gap: tokens.spacingVerticalS,
  },
  fields: {
    display: 'grid',
    gap: tokens.spacingVerticalM,
    border: 0,
    padding: 0,
    margin: 0,
    minWidth: 0,
  },
  expense: {
    display: 'grid',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalM,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    borderRadius: tokens.borderRadiusMedium,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
});

export function WorkActuals({
  workId,
  services,
}: {
  workId: number;
  services: WorkActualsServices;
}) {
  const styles = useStyles();
  const [receipts, setReceipts] = useState<{ id: number; label: string }[]>([]);
  const [data, setData] = useState<ActualsResponse>();
  const [facts, setFacts] = useState<ActualFacts>({ expenses: [] });
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [queued, setQueued] = useState(false);
  const operation = useRef<{ input: string; id: string } | undefined>(
    undefined
  );
  const install = (value: ActualsResponse) => {
    setData(value);
    setFacts(
      value.pendingSubmission?.facts ??
        value.history[value.history.length - 1]?.data.facts ?? { expenses: [] }
    );
    setReason(
      value.pendingSubmission?.reason ??
        value.history[value.history.length - 1]?.data.reason ??
        ''
    );
    setQueued(!!value.pendingSubmission);
    if (value.pendingSubmission)
      setMessage(
        'Submission saved for upload. Use Sync to upload or resolve conflicts before editing.'
      );
  };
  useEffect(() => {
    const controller = new AbortController();
    setData(undefined);
    setError('');
    setQueued(false);
    services
      .loadReceipts(controller, workId)
      .then((value) => {
        if (!controller.signal.aborted) setReceipts(value);
      })
      .catch(() => {});
    services
      .load(controller, workId)
      .then((value) => {
        if (!controller.signal.aborted) install(value);
      })
      .catch((e) => {
        if (!controller.signal.aborted) setError(e.message);
      });
    return () => controller.abort();
  }, [workId, services]);
  useEffect(() => {
    if (!queued || !services.subscribeToSync) return;
    const controller = new AbortController();
    const submittedOperationId =
      operation.current?.id ?? data?.pendingSubmission?.operationId;
    const refreshSubmitted = async () => {
      try {
        const saved = await services.load(controller, workId);
        if (
          !controller.signal.aborted &&
          !saved.pendingSubmission &&
          saved.history.some(
            (item) => item.operationId === submittedOperationId
          )
        ) {
          install(saved);
          setMessage('Actuals submitted for review.');
        }
      } catch (e) {
        if (!controller.signal.aborted) setError((e as Error).message);
      }
    };
    const unsubscribe = services.subscribeToSync(() => {
      void refreshSubmitted();
    });
    // Covers sync finishing immediately before this listener was attached.
    void refreshSubmitted();
    return () => {
      controller.abort();
      unsubscribe();
    };
  }, [queued, services, workId, data?.pendingSubmission?.operationId]);
  const reload = async () => {
    setBusy(true);
    setError('');
    try {
      setReceipts(await services.loadReceipts(new AbortController(), workId));
      install(await services.load(new AbortController(), workId));
      setMessage('');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    if (!data) return;
    setBusy(true);
    setError('');
    setMessage('');
    const input = JSON.stringify({
      expectedRevision: data.revision,
      facts,
      reason,
    });
    if (operation.current?.input !== input)
      operation.current = { input, id: crypto.randomUUID() };
    try {
      const result = await services.save(
        new AbortController(),
        workId,
        {
          operationId: operation.current!.id,
          expectedRevision: data.revision,
          facts,
          reason,
        },
        data.canReview
      );
      if ('queued' in result) {
        setQueued(true);
        setMessage(
          'Saved for upload. Sync changes to submit these actuals for review.'
        );
      } else {
        install(result);
        setMessage(
          data.canReview ? 'Actuals approved.' : 'Actuals submitted for review.'
        );
      }
    } catch (e) {
      setError(
        `${
          (e as Error).message
        } Your entries are retained. For a revision conflict, copy your changes before reloading.`
      );
    } finally {
      setBusy(false);
    }
  };
  const changeExpense = (id: string, change: Partial<ActualExpense>) =>
    setFacts((f) => ({
      ...f,
      expenses: f.expenses.map((e) => (e.id === id ? { ...e, ...change } : e)),
    }));
  const latestRevision = data?.history[data.history.length - 1];
  const hasSubmission = !!data?.history.some(
    (item) => item.kind === 'Submitted'
  );
  const waitingForSubmission = !!data?.canReview && !hasSubmission;
  const editable =
    !!data && (data.canSubmit || (data.canReview && hasSubmission)) && !queued;
  return (
    <section aria-label="Inspection actuals">
      <TitledCard
        title={
          data?.canReview
            ? 'Reported actuals and reviewer adjustments'
            : 'Reported actuals'
        }
        icon={<ClipboardTask20Regular />}
      >
        <div className={styles.content}>
          {error && (
            <MessageBar intent="error">
              <MessageBarBody>{error}</MessageBarBody>
            </MessageBar>
          )}
          {message && (
            <MessageBar intent="info">
              <MessageBarBody>{message}</MessageBarBody>
            </MessageBar>
          )}
          {data?.pendingSubmission && (
            <MessageBar intent="info">
              <MessageBarBody>
                Submission saved for upload. Use Sync to upload or resolve
                conflicts before editing.
              </MessageBarBody>
            </MessageBar>
          )}
          {!data && !error && <ContentSkeleton variant="form" rows={4} />}
          {data && !data.config.fields.length && (
            <p>Actuals are not configured for this Work form.</p>
          )}
          {data && data.config.fields.length > 0 && (
            <>
              <div className={styles.status}>
                <p>
                  Revision {data.revision}:{' '}
                  {data.isApproved
                    ? 'Approved'
                    : data.revision === 0
                    ? 'No actuals submitted'
                    : 'Awaiting review'}
                  . Report work performed separately from requested services and
                  consent.
                </p>
                {latestRevision && (
                  <p>
                    <strong>
                      {latestRevision.kind === 'Submitted'
                        ? 'Submitted reason: '
                        : 'Review reason: '}
                    </strong>
                    {latestRevision.data.reason}
                  </p>
                )}
                {!data.canReview && data.revision > 0 && !queued && (
                  <MessageBar intent="info">
                    <MessageBarBody>
                      Your actuals have been submitted
                      {data.isApproved ? ' and approved' : ' for QA review'}.
                      Finish the required report and photos, then complete the
                      inspection and submit the Work for review. Submitting
                      actuals does not change the Work status. Only submit again
                      if you need to correct the quantities.
                    </MessageBarBody>
                  </MessageBar>
                )}
                {waitingForSubmission && (
                  <MessageBar intent="info">
                    <MessageBarBody>
                      The assigned contractor must submit actuals before QA can
                      review or approve them. In contractor web, open this Work
                      → Actuals → Submit actuals, then sync any queued
                      submission. Return here and reload to review the submitted
                      quantities.
                    </MessageBarBody>
                  </MessageBar>
                )}
              </div>
              <fieldset
                aria-label="Reported actuals"
                disabled={!editable || busy}
                className={styles.fields}
              >
                {data.config.fields
                  .filter(
                    (k) => !['visit', 'expenses', 'returnTripCause'].includes(k)
                  )
                  .map((key) => {
                    const name = key as
                      | 'anchorCount'
                      | 'waitHours'
                      | 'equipmentHours'
                      | 'propertyHours'
                      | 'travelHours';
                    return (
                      <Field key={name} label={actualFields[name]}>
                        <Input
                          type="number"
                          min={0}
                          max={name === 'anchorCount' ? 100000 : 10000}
                          step={name === 'anchorCount' ? 1 : 0.0001}
                          value={facts[name]?.toString() ?? ''}
                          onChange={(_, v) =>
                            setFacts((f) => ({
                              ...f,
                              [name]: v.value === '' ? null : Number(v.value),
                            }))
                          }
                        />
                      </Field>
                    );
                  })}
                {data.config.fields.includes('visit') && (
                  <>
                    <Field label="Visit instant (include UTC offset, e.g. 2026-10-29T10:00:00+11:00)">
                      <Input
                        value={facts.visitedAt ?? ''}
                        onChange={(_, v) =>
                          setFacts((f) => ({
                            ...f,
                            visitedAt: v.value || null,
                          }))
                        }
                      />
                    </Field>
                    <Field label="Visit timezone (e.g. Australia/Sydney)">
                      <Input
                        value={facts.timezoneId ?? ''}
                        onChange={(_, v) =>
                          setFacts((f) => ({
                            ...f,
                            timezoneId: v.value || null,
                          }))
                        }
                      />
                    </Field>
                  </>
                )}
                {data.config.fields.includes('returnTripCause') && (
                  <Field label="Return-trip cause">
                    <Select
                      value={facts.returnTripCause ?? ''}
                      onChange={(_, v) =>
                        setFacts((f) => ({
                          ...f,
                          returnTripCause: v.value || null,
                        }))
                      }
                    >
                      <option value="">Not reported</option>
                      <option value="ClientSite">
                        Client / site responsibility
                      </option>
                      <option value="QV">QV responsibility</option>
                      <option value="Inspector">Inspector omission</option>
                      <option value="Unresolved">Unresolved</option>
                    </Select>
                  </Field>
                )}
                {data.config.fields.includes('expenses') && (
                  <>
                    <Text weight="semibold" size={400}>
                      Receipts and expenses
                    </Text>
                    <Text>
                      Upload receipt images using Photos. Choose the receipt
                      below. Sync new images before linking them. Purchase
                      authorization comes from the Order.
                    </Text>
                    {facts.expenses.map((expense, index) => (
                      <fieldset
                        key={expense.id}
                        className={styles.expense}
                        aria-label={`Expense ${index + 1}`}
                      >
                        <Text weight="semibold">Expense {index + 1}</Text>
                        <Field label="Expense type">
                          <Select
                            value={expense.kind}
                            onChange={(_, v) =>
                              changeExpense(expense.id, {
                                kind: v.value as ActualExpense['kind'],
                              })
                            }
                          >
                            <option value="Purchase">Purchase</option>
                            <option value="OvernightShipping">
                              Overnight shipping
                            </option>
                          </Select>
                        </Field>
                        <Field label="Receipt amount">
                          <Input
                            type="number"
                            min={0}
                            step={0.01}
                            value={String(expense.amount)}
                            onChange={(_, v) =>
                              changeExpense(expense.id, {
                                amount: Number(v.value),
                              })
                            }
                          />
                        </Field>
                        <Field label="Currency">
                          <Input
                            maxLength={3}
                            value={expense.currency}
                            onChange={(_, v) =>
                              changeExpense(expense.id, {
                                currency: v.value.toUpperCase(),
                              })
                            }
                          />
                        </Field>
                        <Field label="Receipt image">
                          <Select
                            value={expense.receiptImageId?.toString() ?? ''}
                            onChange={(_, v) =>
                              changeExpense(expense.id, {
                                receiptImageId: v.value
                                  ? Number(v.value)
                                  : null,
                              })
                            }
                          >
                            <option value="">Receipt not attached</option>
                            {expense.receiptImageId &&
                              !receipts.some(
                                (r) => r.id === expense.receiptImageId
                              ) && (
                                <option value={expense.receiptImageId}>
                                  Photo {expense.receiptImageId}
                                </option>
                              )}
                            {receipts
                              .filter((r) => r.id > 0)
                              .map((r) => (
                                <option key={r.id} value={r.id}>
                                  {r.label} (#{r.id})
                                </option>
                              ))}
                          </Select>
                        </Field>
                        <Button
                          onClick={() =>
                            setFacts((f) => ({
                              ...f,
                              expenses: f.expenses.filter(
                                (e) => e.id !== expense.id
                              ),
                            }))
                          }
                        >
                          Remove expense {index + 1}
                        </Button>
                      </fieldset>
                    ))}
                    <Button
                      onClick={() =>
                        setFacts((f) => ({
                          ...f,
                          expenses: [
                            ...f.expenses,
                            {
                              id: crypto.randomUUID(),
                              kind: 'Purchase',
                              amount: 0,
                              currency: '',
                            },
                          ],
                        }))
                      }
                    >
                      Add expense
                    </Button>
                  </>
                )}
                <Field
                  label={
                    data.canReview
                      ? 'Review and adjustment reason'
                      : 'Submission or correction reason'
                  }
                  required
                >
                  <Textarea
                    value={reason}
                    maxLength={1000}
                    onChange={(_, v) => setReason(v.value)}
                  />
                </Field>
              </fieldset>
              {data.canReview && (
                <section aria-label="Captured timing evidence">
                  <Text weight="semibold" size={400}>
                    App timing evidence
                  </Text>
                  <Text>
                    Shown for comparison. Elapsed time does not automatically
                    determine pay or client billing.
                  </Text>
                  {data.timingEvidence.length ? (
                    <ul>
                      {data.timingEvidence.map((e) => (
                        <li key={`${e.eventType}-${e.id}`}>
                          {e.eventType}: {e.eventAt} UTC
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No captured inspection timing is available.</p>
                  )}
                </section>
              )}
              <section aria-label="Actuals history">
                <Text weight="semibold" size={400}>
                  Revision history
                </Text>
                {[...data.history].reverse().map((h) => (
                  <details key={h.revision}>
                    <summary>
                      Revision {h.revision} — {h.kind} — user {h.createdBy} —{' '}
                      {h.createdAt} UTC
                    </summary>
                    <p>{h.data.reason}</p>
                    <dl>
                      {Object.entries(actualFields)
                        .filter(([k]) => !['expenses', 'visit'].includes(k))
                        .map(([k, label]) => (
                          <div key={k}>
                            <dt>{label}</dt>
                            <dd>
                              {String(
                                h.data.facts[k as keyof ActualFacts] ??
                                  'Not reported'
                              )}
                            </dd>
                          </div>
                        ))}
                      <dt>Visit</dt>
                      <dd>
                        {h.data.facts.visitedAt ?? 'Not reported'}{' '}
                        {h.data.facts.timezoneId}
                      </dd>
                    </dl>
                    {h.data.facts.expenses.map((e) => (
                      <p key={e.id}>
                        {e.kind}: {e.amount} {e.currency}; receipt image{' '}
                        {e.receiptImageId ?? 'missing'}
                      </p>
                    ))}
                    {data.canReview && (
                      <>
                        <p>
                          Authorized purchase cap:{' '}
                          {h.data.purchaseCap ?? 'Unresolved'}{' '}
                          {h.data.purchaseCurrency}
                        </p>
                        {h.data.reviewedExpenses.map((e) => (
                          <p key={e.id}>
                            Receipt {e.id}: actual {e.actualAmount};
                            reimbursable {e.reimbursableAmount}; excess{' '}
                            {e.excessAmount} {e.currency}
                          </p>
                        ))}
                      </>
                    )}
                  </details>
                ))}
              </section>
            </>
          )}
          <div className={styles.actions}>
            <Button disabled={busy} onClick={reload}>
              Cancel
            </Button>
            {!!data?.config.fields.length && (
              <Button
                appearance="primary"
                disabled={
                  !editable || busy || !reason.trim() || waitingForSubmission
                }
                onClick={save}
              >
                {data.canReview ? 'Approve reviewed actuals' : 'Submit actuals'}
              </Button>
            )}
          </div>
        </div>
      </TitledCard>
    </section>
  );
}
